import React, { useState, useEffect, useRef } from 'react';
import TopNav from '../components/Layout/TopNav.jsx';
import useFundamentalSignals from '../hooks/useFundamentalSignals.js';
import '../styles/FundamentalSignals.css';

function FundamentalSignals() {
  const {
    recommendations,
    loading,
    error,
    currentMarket,
    setCurrentMarket,
    currentFilter,
    setCurrentFilter,
    recommendationCounts,
    marketCounts,
    refresh,
    fetchStockDetails
  } = useFundamentalSignals();

  const [selectedStock, setSelectedStock] = useState(null);
  const [stockDetails, setStockDetails] = useState({ fundamentals: null, wavetrendData: { strategies: [], ticker_quality_score: null }, peerGroup: null });
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [activeTab, setActiveTab] = useState('peer'); // peer, sector, momentum

  // Handle URL query params for direct links (only once on mount)
  const hasHandledUrlParam = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ticker = params.get('ticker');

    // Only handle URL param once when recommendations are loaded
    if (ticker && recommendations.length > 0 && !hasHandledUrlParam.current) {
      const stock = recommendations.find(r => r.ticker.toUpperCase() === ticker.toUpperCase());
      if (stock) {
        hasHandledUrlParam.current = true;
        handleStockClick(stock);
      }
    }
  }, [recommendations]);

  const handleStockClick = async (stock) => {
    setSelectedStock(stock);
    setDetailsLoading(true);

    // Update URL with ticker parameter
    const url = new URL(window.location.href);
    url.searchParams.set('ticker', stock.ticker);
    window.history.pushState({}, '', url);

    const details = await fetchStockDetails(stock.ticker, stock.market || currentMarket);
    setStockDetails(details);
    setDetailsLoading(false);
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return '--';
    const num = parseFloat(value);
    return `${(num * 100).toFixed(1)}%`;
  };

  const formatNumber = (value, decimals = 1) => {
    if (!value && value !== 0) return '--';
    const num = parseFloat(value);
    return num.toFixed(decimals);
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '--';
    const num = parseFloat(value);
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toFixed(0)}`;
  };

  // Statistical outlier detection for peer comparison
  const getPeerOutlierStatus = (currentValue, peerValues, lowerIsBetter = false) => {
    // Handle missing/invalid values
    if (!currentValue && currentValue !== 0) {
      return {
        status: 'unknown',
        zScore: null,
        showBadge: false,
        badgeText: '',
        icon: ''
      };
    }

    // Filter valid values and include current stock
    const allValues = [currentValue, ...peerValues].filter(v => v !== null && v !== undefined && !isNaN(v));

    // Need at least 3 values for meaningful statistics
    if (allValues.length < 3) {
      return {
        status: 'insufficient-data',
        zScore: null,
        showBadge: false,
        badgeText: '--',
        icon: ''
      };
    }

    // Calculate mean
    const mean = allValues.reduce((sum, v) => sum + v, 0) / allValues.length;

    // Calculate standard deviation
    const squaredDiffs = allValues.map(v => Math.pow(v - mean, 2));
    const variance = squaredDiffs.reduce((sum, v) => sum + v, 0) / allValues.length;
    const stdDev = Math.sqrt(variance);

    // Handle edge case: all values very similar (low variance)
    // If std dev is less than 10% of mean, consider all values normal
    if (stdDev < Math.abs(mean) * 0.1) {
      return {
        status: 'normal',
        zScore: 0,
        showBadge: true,
        badgeText: 'Normal ✅',
        icon: '✅'
      };
    }

    // Calculate z-score (how many standard deviations from mean)
    const zScore = (currentValue - mean) / stdDev;
    const absZ = Math.abs(zScore);

    // Determine if this is good or bad based on metric direction
    let isGoodDirection;
    if (lowerIsBetter) {
      // For P/E, P/B, P/S: lower values are better
      isGoodDirection = zScore < 0; // Below mean is good
    } else {
      // For ROE, Margin: higher values are better
      isGoodDirection = zScore > 0; // Above mean is good
    }

    // Classify outlier severity
    let status, badgeText, icon;

    if (absZ <= 1.5) {
      // Normal range
      status = 'normal';
      badgeText = 'Normal ✅';
      icon = '✅';
    } else if (absZ <= 2.0) {
      // Mild outlier
      if (isGoodDirection) {
        status = 'mild-good';
        badgeText = `Good (${zScore > 0 ? '+' : ''}${zScore.toFixed(1)}σ) ⭐`;
        icon = '⭐';
      } else {
        status = 'mild-bad';
        badgeText = `Caution (${zScore > 0 ? '+' : ''}${zScore.toFixed(1)}σ) ⚠️`;
        icon = '⚠️';
      }
    } else {
      // Strong outlier
      if (isGoodDirection) {
        status = 'strong-good';
        badgeText = `Exceptional (${zScore > 0 ? '+' : ''}${zScore.toFixed(1)}σ) ⭐`;
        icon = '⭐';
      } else {
        status = 'strong-bad';
        badgeText = `Concerning (${zScore > 0 ? '+' : ''}${zScore.toFixed(1)}σ) ⚠️`;
        icon = '⚠️';
      }
    }

    return {
      status,
      zScore,
      showBadge: true,
      badgeText,
      icon
    };
  };

  const getRecommendationBadgeClass = (rec) => {
    const classMap = {
      'STRONG_BUY': 'rec-strong-buy',
      'BUY': 'rec-buy',
      'BUY_FUNDAMENTALS': 'rec-buy-fundamentals',
      'BUY_MOMENTUM': 'rec-buy-momentum',
      'BUY_LOW_CONFIDENCE': 'rec-buy-low-confidence',
      'HOLD_QUALITY': 'rec-hold-quality',
      'HOLD_MOMENTUM': 'rec-hold-momentum',
      'HOLD': 'rec-hold',
      'SELL': 'rec-sell',
      'AVOID': 'rec-avoid'
    };
    return `rec-badge ${classMap[rec] || ''}`;
  };

  const getPercentileColor = (value) => {
    if (!value) return 'percentile-low';
    if (value >= 75) return 'percentile-high';
    if (value >= 50) return 'percentile-medium';
    return 'percentile-low';
  };

  const getQualitativeLabel = (percentile) => {
    if (!percentile && percentile !== 0) return { text: 'N/A', emoji: '', className: 'qual-na' };
    if (percentile >= 85) return { text: 'EXCELLENT', emoji: '⭐⭐⭐⭐⭐', className: 'qual-excellent' };
    if (percentile >= 65) return { text: 'GOOD', emoji: '⭐⭐⭐⭐☆', className: 'qual-good' };
    if (percentile >= 45) return { text: 'AVERAGE', emoji: '⭐⭐⭐☆☆', className: 'qual-average' };
    if (percentile >= 25) return { text: 'POOR', emoji: '⭐⭐☆☆☆', className: 'qual-poor' };
    return { text: 'VERY POOR', emoji: '⭐☆☆☆☆', className: 'qual-very-poor' };
  };

  const metricTooltips = {
    'pe_ratio': 'Price-to-Earnings ratio compared to sector peers. Tech stocks naturally have higher P/E than banks. Ranking shows how this stock compares to others in its sector.',
    'peg_ratio': 'PE ratio divided by earnings growth rate, compared to sector peers. Accounts for growth expectations within the sector.',
    'price_to_book': 'Stock price vs book value, compared to sector peers. Tech stocks naturally have high P/B (software assets), banks have low P/B (physical assets). This ranking compares within the sector only.',
    'price_to_sales': 'Market cap divided by revenue, compared to sector peers. Shows valuation relative to sales within the same industry.',
    'roe': 'Return on Equity. How efficiently the company uses shareholder money. Higher is better. >15% is good.',
    'profit_margin': 'Net income as % of revenue. Higher is better. Shows profitability.',
    'operating_margin': 'Operating income as % of revenue. Higher is better. Shows operational efficiency.',
    'debt_to_equity': 'Total debt divided by shareholders\' equity. Lower is better. <0.5 is conservative, 0.5-1.5 is moderate, >1.5 is aggressive.',
    'revenue_growth': 'Annual revenue growth rate. Higher is better. >10% is strong growth.',
    'earnings_growth': 'Annual earnings growth rate. Higher is better. >10% is strong growth.'
  };

  return (
    <div className="app fundamental-signals-page">
      <TopNav onRefresh={refresh} title="Fundamental + Momentum Analysis" />

      <div className="container">
        {/* Market Tabs */}
        <div className="market-tabs">
          <button
            className={`market-tab ${currentMarket === 'US' ? 'active' : ''}`}
            onClick={() => setCurrentMarket('US')}
          >
            🇺🇸 US Stocks ({marketCounts.US})
          </button>
          <button
            className={`market-tab ${currentMarket === 'IN' ? 'active' : ''}`}
            onClick={() => setCurrentMarket('IN')}
          >
            🇮🇳 India ({marketCounts.IN})
          </button>
          <button
            className={`market-tab ${currentMarket === 'TEST' ? 'active' : ''}`}
            onClick={() => setCurrentMarket('TEST')}
          >
            🧪 Test Data ({marketCounts.TEST})
          </button>
        </div>

        {/* Recommendation Filter Tabs */}
        <div className="filter-tabs">
          <button
            className={`filter-tab ${currentFilter === 'ALL' ? 'active' : ''}`}
            onClick={() => setCurrentFilter('ALL')}
          >
            All ({recommendations.filter(r => r.market === currentMarket).length})
          </button>
          <button
            className={`filter-tab ${currentFilter === 'STRONG_BUY' ? 'active' : ''}`}
            onClick={() => setCurrentFilter('STRONG_BUY')}
          >
            Strong Buy ({recommendationCounts.STRONG_BUY})
          </button>
          <button
            className={`filter-tab ${currentFilter === 'BUY_FUNDAMENTALS' ? 'active' : ''}`}
            onClick={() => setCurrentFilter('BUY_FUNDAMENTALS')}
          >
            Buy - Fundamentals ({recommendationCounts.BUY_FUNDAMENTALS})
          </button>
          <button
            className={`filter-tab ${currentFilter === 'BUY_MOMENTUM' ? 'active' : ''}`}
            onClick={() => setCurrentFilter('BUY_MOMENTUM')}
          >
            Buy - Momentum ({recommendationCounts.BUY_MOMENTUM})
          </button>
          <button
            className={`filter-tab ${currentFilter === 'BUY_LOW_CONFIDENCE' ? 'active' : ''}`}
            onClick={() => setCurrentFilter('BUY_LOW_CONFIDENCE')}
          >
            Buy - Low Confidence ({recommendationCounts.BUY_LOW_CONFIDENCE})
          </button>
          <button
            className={`filter-tab ${currentFilter === 'HOLD_QUALITY' ? 'active' : ''}`}
            onClick={() => setCurrentFilter('HOLD_QUALITY')}
          >
            Hold - Quality ({recommendationCounts.HOLD_QUALITY})
          </button>
          <button
            className={`filter-tab ${currentFilter === 'HOLD_MOMENTUM' ? 'active' : ''}`}
            onClick={() => setCurrentFilter('HOLD_MOMENTUM')}
          >
            Hold - Momentum ({recommendationCounts.HOLD_MOMENTUM})
          </button>
          <button
            className={`filter-tab ${currentFilter === 'HOLD' ? 'active' : ''}`}
            onClick={() => setCurrentFilter('HOLD')}
          >
            Hold ({recommendationCounts.HOLD})
          </button>
          <button
            className={`filter-tab ${currentFilter === 'AVOID' ? 'active' : ''}`}
            onClick={() => setCurrentFilter('AVOID')}
          >
            Avoid ({recommendationCounts.AVOID})
          </button>
        </div>

        {/* Loading / Error States */}
        {loading && <div className="loading">Loading recommendations...</div>}
        {error && <div className="error">Error: {error}</div>}

        {/* Recommendations Table */}
        {!loading && !error && (
          <div className="table-container">
            <table className="recommendations-table">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Recommendation</th>
                  <th>Fundamental %</th>
                  <th>Momentum %</th>
                  <th>Quality %</th>
                  <th>Value %</th>
                  <th>Growth %</th>
                </tr>
              </thead>
              <tbody>
                {recommendations.map(stock => (
                  <tr key={stock.ticker} onClick={() => handleStockClick(stock)}>
                    <td className="ticker-cell">{stock.ticker}</td>
                    <td>
                      <span className={getRecommendationBadgeClass(stock.recommendation)}>
                        {stock.recommendation.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>
                      <div className="percentile-container">
                        <div className="percentile-bar">
                          <div
                            className={`percentile-fill ${getPercentileColor(stock.fundamental_percentile)}`}
                            style={{ width: `${stock.fundamental_percentile || 0}%` }}
                          ></div>
                        </div>
                        <span className="percentile-text">
                          {stock.fundamental_percentile ? parseFloat(stock.fundamental_percentile).toFixed(1) : null || '--'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="percentile-container">
                        <div className="percentile-bar">
                          <div
                            className={`percentile-fill ${getPercentileColor(stock.momentum_percentile)}`}
                            style={{ width: `${stock.momentum_percentile || 0}%` }}
                          ></div>
                        </div>
                        <span className="percentile-text">
                          {stock.momentum_percentile ? parseFloat(stock.momentum_percentile).toFixed(1) : null || '--'}
                        </span>
                      </div>
                    </td>
                    <td className="percentile-text">{stock.quality_percentile ? parseFloat(stock.quality_percentile).toFixed(1) : null || '--'}</td>
                    <td className="percentile-text">{stock.value_percentile ? parseFloat(stock.value_percentile).toFixed(1) : null || '--'}</td>
                    <td className="percentile-text">{stock.growth_percentile ? parseFloat(stock.growth_percentile).toFixed(1) : null || '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail Modal */}
        {selectedStock && (
          <div className="modal" onClick={() => setSelectedStock(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h2>
                    {selectedStock.ticker}
                    {stockDetails.fundamentals?.sector && (
                      <span style={{ fontSize: '16px', fontWeight: 'normal', color: '#8b949e', marginLeft: '12px' }}>
                        • {stockDetails.fundamentals.sector}
                      </span>
                    )}
                  </h2>
                  <span className={getRecommendationBadgeClass(selectedStock.recommendation)}>
                    {selectedStock.recommendation.replace(/_/g, ' ')}
                  </span>
                </div>
                <button className="modal-close" onClick={() => setSelectedStock(null)}>×</button>
              </div>

              {/* Tab Navigation */}
              <div className="modal-tabs">
                <button
                  className={`modal-tab ${activeTab === 'peer' ? 'active' : ''}`}
                  onClick={() => setActiveTab('peer')}
                >
                  🤝 Peer Comparison
                </button>
                <button
                  className={`modal-tab ${activeTab === 'sector' ? 'active' : ''}`}
                  onClick={() => setActiveTab('sector')}
                >
                  🏢 Sector Comparison
                </button>
                <button
                  className={`modal-tab ${activeTab === 'momentum' ? 'active' : ''}`}
                  onClick={() => setActiveTab('momentum')}
                >
                  📈 Momentum Signals
                </button>
              </div>

{detailsLoading ? (
                <div className="loading">Loading detailed data...</div>
              ) : (
                <>
                  {/* PEER TAB - Peer Comparison */}
                  {activeTab === 'peer' && stockDetails.fundamentals && (
                    <div className="modal-section">
                      <h3>🤝 Peer Comparison</h3>

                      {stockDetails.peerGroup && stockDetails.peerGroup.peers && stockDetails.peerGroup.peers.length > 0 ? (
                        <>
                          {/* Peer Info */}
                          <div className="peer-explanation" style={{ marginBottom: '16px' }}>
                            <strong>Comparing against {stockDetails.peerGroup.peers.length} peers</strong> • {stockDetails.peerGroup.method === 'manual' ? '✋ Manually curated peer group' : '🤖 Algorithmically selected'}
                            <br />
                            <span style={{ fontSize: '13px', color: '#8b949e' }}>{stockDetails.peerGroup.reasoning}</span>
                          </div>

                          {/* Metrics in line-by-line format like Sector tab */}
                          <h4 className="subsection-title">COMPARISON VS PEERS</h4>
                          <div className="raw-metrics-grid">
                            <div className="raw-metric">
                              <span className="raw-label">
                                PE Ratio
                                <span
                                  className="tooltip-icon"
                                  onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'peer_pe' ? null : 'peer_pe'); }}
                                >
                                  ⓘ
                                </span>
                                {activeTooltip === 'peer_pe' && (
                                  <span className="tooltip-popup">{metricTooltips.pe_ratio}</span>
                                )}
                              </span>
                              <span className="raw-value">{formatNumber(stockDetails.fundamentals.pe_ratio, 1)}</span>
                              {(() => {
                                const peers = stockDetails.peerGroup.peers || [];
                                const status = getPeerOutlierStatus(stockDetails.fundamentals.pe_ratio, peers.map(p => p.pe_ratio), true);
                                return status.showBadge ? (
                                  <span className={`qual-label ${status.status.includes('good') ? 'qual-good' : status.status.includes('bad') ? 'qual-poor' : 'qual-average'}`}>
                                    {status.badgeText}
                                  </span>
                                ) : null;
                              })()}
                            </div>

                            <div className="raw-metric">
                              <span className="raw-label">
                                Price/Book
                                <span
                                  className="tooltip-icon"
                                  onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'peer_pb' ? null : 'peer_pb'); }}
                                >
                                  ⓘ
                                </span>
                                {activeTooltip === 'peer_pb' && (
                                  <span className="tooltip-popup">{metricTooltips.price_to_book}</span>
                                )}
                              </span>
                              <span className="raw-value">{formatNumber(stockDetails.fundamentals.price_to_book, 1)}</span>
                              {(() => {
                                const peers = stockDetails.peerGroup.peers || [];
                                const status = getPeerOutlierStatus(stockDetails.fundamentals.price_to_book, peers.map(p => p.price_to_book), true);
                                return status.showBadge ? (
                                  <span className={`qual-label ${status.status.includes('good') ? 'qual-good' : status.status.includes('bad') ? 'qual-poor' : 'qual-average'}`}>
                                    {status.badgeText}
                                  </span>
                                ) : null;
                              })()}
                            </div>

                            <div className="raw-metric">
                              <span className="raw-label">
                                Price/Sales
                                <span
                                  className="tooltip-icon"
                                  onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'peer_ps' ? null : 'peer_ps'); }}
                                >
                                  ⓘ
                                </span>
                                {activeTooltip === 'peer_ps' && (
                                  <span className="tooltip-popup">{metricTooltips.price_to_sales}</span>
                                )}
                              </span>
                              <span className="raw-value">{formatNumber(stockDetails.fundamentals.price_to_sales, 1)}</span>
                              {(() => {
                                const peers = stockDetails.peerGroup.peers || [];
                                const status = getPeerOutlierStatus(stockDetails.fundamentals.price_to_sales, peers.map(p => p.price_to_sales), true);
                                return status.showBadge ? (
                                  <span className={`qual-label ${status.status.includes('good') ? 'qual-good' : status.status.includes('bad') ? 'qual-poor' : 'qual-average'}`}>
                                    {status.badgeText}
                                  </span>
                                ) : null;
                              })()}
                            </div>

                            <div className="raw-metric">
                              <span className="raw-label">
                                ROE (Return on Equity)
                                <span
                                  className="tooltip-icon"
                                  onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'peer_roe' ? null : 'peer_roe'); }}
                                >
                                  ⓘ
                                </span>
                                {activeTooltip === 'peer_roe' && (
                                  <span className="tooltip-popup">{metricTooltips.roe}</span>
                                )}
                              </span>
                              <span className="raw-value">{formatPercent(stockDetails.fundamentals.roe)}</span>
                              {(() => {
                                const peers = stockDetails.peerGroup.peers || [];
                                const status = getPeerOutlierStatus(stockDetails.fundamentals.roe, peers.map(p => p.roe), false);
                                return status.showBadge ? (
                                  <span className={`qual-label ${status.status.includes('good') ? 'qual-good' : status.status.includes('bad') ? 'qual-poor' : 'qual-average'}`}>
                                    {status.badgeText}
                                  </span>
                                ) : null;
                              })()}
                            </div>

                            <div className="raw-metric">
                              <span className="raw-label">
                                Profit Margin
                                <span
                                  className="tooltip-icon"
                                  onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'peer_margin' ? null : 'peer_margin'); }}
                                >
                                  ⓘ
                                </span>
                                {activeTooltip === 'peer_margin' && (
                                  <span className="tooltip-popup">{metricTooltips.profit_margin}</span>
                                )}
                              </span>
                              <span className="raw-value">{formatPercent(stockDetails.fundamentals.profit_margin)}</span>
                              {(() => {
                                const peers = stockDetails.peerGroup.peers || [];
                                const status = getPeerOutlierStatus(stockDetails.fundamentals.profit_margin, peers.map(p => p.profit_margin), false);
                                return status.showBadge ? (
                                  <span className={`qual-label ${status.status.includes('good') ? 'qual-good' : status.status.includes('bad') ? 'qual-poor' : 'qual-average'}`}>
                                    {status.badgeText}
                                  </span>
                                ) : null;
                              })()}
                            </div>
                          </div>

                          {/* Detailed Peer Table */}
                          <h4 className="subsection-title" style={{ marginTop: '24px' }}>PEER GROUP DETAILS</h4>
                          <div className="peer-comparison-table-container">
                            <table className="peer-comparison-table">
                              <thead>
                                <tr>
                                  <th>Ticker</th>
                                  <th>Industry</th>
                                  <th>Mkt Cap</th>
                                  <th>PE</th>
                                  <th>P/B</th>
                                  <th>P/S</th>
                                  <th>ROE</th>
                                  <th>Margin</th>
                                  {stockDetails.peerGroup.method === 'algorithmic' && <th>Match %</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {/* Current Stock (highlighted) */}
                                {(() => {
                                  const peers = stockDetails.peerGroup.peers || [];
                                  const peStatus = getPeerOutlierStatus(stockDetails.fundamentals.pe_ratio, peers.map(p => p.pe_ratio), true);
                                  const pbStatus = getPeerOutlierStatus(stockDetails.fundamentals.price_to_book, peers.map(p => p.price_to_book), true);
                                  const psStatus = getPeerOutlierStatus(stockDetails.fundamentals.price_to_sales, peers.map(p => p.price_to_sales), true);
                                  const roeStatus = getPeerOutlierStatus(stockDetails.fundamentals.roe, peers.map(p => p.roe), false);
                                  const marginStatus = getPeerOutlierStatus(stockDetails.fundamentals.profit_margin, peers.map(p => p.profit_margin), false);

                                  return (
                                    <tr className="current-stock-row">
                                      <td><strong>{selectedStock.ticker}</strong></td>
                                      <td className="industry-cell">{stockDetails.fundamentals.industry?.substring(0, 20) || '--'}</td>
                                      <td>{formatCurrency(stockDetails.fundamentals.market_cap)}</td>
                                      <td className={`peer-outlier-${peStatus.status}`}>
                                        {formatNumber(stockDetails.fundamentals.pe_ratio, 1)}
                                        <div style={{ fontSize: '10px', marginTop: '2px' }}>{peStatus.icon}</div>
                                      </td>
                                      <td className={`peer-outlier-${pbStatus.status}`}>
                                        {formatNumber(stockDetails.fundamentals.price_to_book, 1)}
                                        <div style={{ fontSize: '10px', marginTop: '2px' }}>{pbStatus.icon}</div>
                                      </td>
                                      <td className={`peer-outlier-${psStatus.status}`}>
                                        {formatNumber(stockDetails.fundamentals.price_to_sales, 1)}
                                        <div style={{ fontSize: '10px', marginTop: '2px' }}>{psStatus.icon}</div>
                                      </td>
                                      <td className={`peer-outlier-${roeStatus.status}`}>
                                        {formatPercent(stockDetails.fundamentals.roe)}
                                        <div style={{ fontSize: '10px', marginTop: '2px' }}>{roeStatus.icon}</div>
                                      </td>
                                      <td className={`peer-outlier-${marginStatus.status}`}>
                                        {formatPercent(stockDetails.fundamentals.profit_margin)}
                                        <div style={{ fontSize: '10px', marginTop: '2px' }}>{marginStatus.icon}</div>
                                      </td>
                                      {stockDetails.peerGroup.method === 'algorithmic' && <td>--</td>}
                                    </tr>
                                  );
                                })()}
                                {/* Peer stocks - clickable and with outlier icons */}
                                {(() => {
                                  const peers = stockDetails.peerGroup.peers || [];
                                  const allPeers = peers.slice(0, 5);

                                  return allPeers.map((peer, idx) => {
                                    // Calculate outlier status for each peer
                                    const peStatus = getPeerOutlierStatus(peer.pe_ratio, [...peers.filter(p => p.ticker !== peer.ticker).map(p => p.pe_ratio), stockDetails.fundamentals.pe_ratio], true);
                                    const pbStatus = getPeerOutlierStatus(peer.price_to_book, [...peers.filter(p => p.ticker !== peer.ticker).map(p => p.price_to_book), stockDetails.fundamentals.price_to_book], true);
                                    const psStatus = getPeerOutlierStatus(peer.price_to_sales, [...peers.filter(p => p.ticker !== peer.ticker).map(p => p.price_to_sales), stockDetails.fundamentals.price_to_sales], true);
                                    const roeStatus = getPeerOutlierStatus(peer.roe, [...peers.filter(p => p.ticker !== peer.ticker).map(p => p.roe), stockDetails.fundamentals.roe], false);
                                    const marginStatus = getPeerOutlierStatus(peer.profit_margin, [...peers.filter(p => p.ticker !== peer.ticker).map(p => p.profit_margin), stockDetails.fundamentals.profit_margin], false);

                                    return (
                                      <tr
                                        key={peer.ticker}
                                        className="peer-row clickable-row"
                                        onClick={async () => {
                                          const peerStock = recommendations.find(r => r.ticker === peer.ticker);
                                          if (peerStock) {
                                            await handleStockClick(peerStock);
                                          }
                                        }}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <td><strong>{peer.ticker}</strong></td>
                                        <td className="industry-cell">{peer.industry?.substring(0, 20) || '--'}</td>
                                        <td>{formatCurrency(peer.market_cap)}</td>
                                        <td className={`peer-outlier-${peStatus.status}`}>
                                          {formatNumber(peer.pe_ratio, 1)}
                                          <div style={{ fontSize: '10px', marginTop: '2px' }}>{peStatus.icon}</div>
                                        </td>
                                        <td className={`peer-outlier-${pbStatus.status}`}>
                                          {formatNumber(peer.price_to_book, 1)}
                                          <div style={{ fontSize: '10px', marginTop: '2px' }}>{pbStatus.icon}</div>
                                        </td>
                                        <td className={`peer-outlier-${psStatus.status}`}>
                                          {formatNumber(peer.price_to_sales, 1)}
                                          <div style={{ fontSize: '10px', marginTop: '2px' }}>{psStatus.icon}</div>
                                        </td>
                                        <td className={`peer-outlier-${roeStatus.status}`}>
                                          {formatPercent(peer.roe)}
                                          <div style={{ fontSize: '10px', marginTop: '2px' }}>{roeStatus.icon}</div>
                                        </td>
                                        <td className={`peer-outlier-${marginStatus.status}`}>
                                          {formatPercent(peer.profit_margin)}
                                          <div style={{ fontSize: '10px', marginTop: '2px' }}>{marginStatus.icon}</div>
                                        </td>
                                        {stockDetails.peerGroup.method === 'algorithmic' && (
                                          <td className="similarity-score">
                                            {peer.similarity_score ? `${parseFloat(peer.similarity_score).toFixed(0)}%` : '--'}
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  });
                                })()}
                              </tbody>
                            </table>
                          </div>

                          {stockDetails.peerGroup.peers.length > 5 && (
                            <div className="peer-footer">
                              Showing top 5 of {stockDetails.peerGroup.peers.length} peers
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="summary-text">No peer group data available for this stock.</div>
                      )}

                    </div>
                  )}

                  {/* SECTOR TAB - Sector Comparison */}
                  {activeTab === 'sector' && stockDetails.fundamentals && (
                    <div className="modal-section">
                      <h3>🏢 Sector Comparison</h3>

                      <h4 className="subsection-title">
                        QUALITY METRICS
                        <span className="composite-score">
                          Overall Quality: {getQualitativeLabel(selectedStock.quality_percentile).text} {getQualitativeLabel(selectedStock.quality_percentile).emoji} ({selectedStock.quality_percentile ? parseFloat(selectedStock.quality_percentile).toFixed(0) : null}th percentile)
                        </span>
                      </h4>
                      <div className="raw-metrics-grid">
                        <div className="raw-metric">
                          <span className="raw-label">
                            ROE (Return on Equity)
                            <span
                              className="tooltip-icon"
                              onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'roe' ? null : 'roe'); }}
                            >
                              ⓘ
                            </span>
                            {activeTooltip === 'roe' && (
                              <span className="tooltip-popup">{metricTooltips.roe}</span>
                            )}
                          </span>
                          <span className="raw-value">{formatPercent(stockDetails.fundamentals.roe)}</span>
                          {stockDetails.fundamentals.roe_percentile && (
                            <span className={`qual-label ${getQualitativeLabel(stockDetails.fundamentals.roe_percentile).className}`}>
                              {getQualitativeLabel(stockDetails.fundamentals.roe_percentile).text} {getQualitativeLabel(stockDetails.fundamentals.roe_percentile).emoji} - Top {(100-parseFloat(stockDetails.fundamentals.roe_percentile)).toFixed(0)}%
                            </span>
                          )}
                        </div>
                        <div className="raw-metric">
                          <span className="raw-label">
                            Profit Margin
                            <span
                              className="tooltip-icon"
                              onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'profit_margin' ? null : 'profit_margin'); }}
                            >
                              ⓘ
                            </span>
                            {activeTooltip === 'profit_margin' && (
                              <span className="tooltip-popup">{metricTooltips.profit_margin}</span>
                            )}
                          </span>
                          <span className="raw-value">{formatPercent(stockDetails.fundamentals.profit_margin)}</span>
                          {stockDetails.fundamentals.profit_margin_percentile && (
                            <span className={`qual-label ${getQualitativeLabel(stockDetails.fundamentals.profit_margin_percentile).className}`}>
                              {getQualitativeLabel(stockDetails.fundamentals.profit_margin_percentile).text} {getQualitativeLabel(stockDetails.fundamentals.profit_margin_percentile).emoji} - Top {(100-parseFloat(stockDetails.fundamentals.profit_margin_percentile)).toFixed(0)}%
                            </span>
                          )}
                        </div>
                        <div className="raw-metric">
                          <span className="raw-label">
                            Operating Margin
                            <span
                              className="tooltip-icon"
                              onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'operating_margin' ? null : 'operating_margin'); }}
                            >
                              ⓘ
                            </span>
                            {activeTooltip === 'operating_margin' && (
                              <span className="tooltip-popup">{metricTooltips.operating_margin}</span>
                            )}
                          </span>
                          <span className="raw-value">{formatPercent(stockDetails.fundamentals.operating_margin)}</span>
                          {stockDetails.fundamentals.operating_margin_percentile && (
                            <span className={`qual-label ${getQualitativeLabel(stockDetails.fundamentals.operating_margin_percentile).className}`}>
                              {getQualitativeLabel(stockDetails.fundamentals.operating_margin_percentile).text} {getQualitativeLabel(stockDetails.fundamentals.operating_margin_percentile).emoji} - Top {(100-parseFloat(stockDetails.fundamentals.operating_margin_percentile)).toFixed(0)}%
                            </span>
                          )}
                        </div>
                        <div className="raw-metric">
                          <span className="raw-label">
                            Debt/Equity
                            <span
                              className="tooltip-icon"
                              onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'debt_to_equity' ? null : 'debt_to_equity'); }}
                            >
                              ⓘ
                            </span>
                            {activeTooltip === 'debt_to_equity' && (
                              <span className="tooltip-popup">{metricTooltips.debt_to_equity}</span>
                            )}
                          </span>
                          <span className="raw-value">{formatNumber(stockDetails.fundamentals.debt_to_equity, 2)}</span>
                          {stockDetails.fundamentals.debt_to_equity_percentile && (
                            <span className={`qual-label ${getQualitativeLabel(stockDetails.fundamentals.debt_to_equity_percentile).className}`}>
                              {getQualitativeLabel(stockDetails.fundamentals.debt_to_equity_percentile).text} {getQualitativeLabel(stockDetails.fundamentals.debt_to_equity_percentile).emoji} - Top {(100-parseFloat(stockDetails.fundamentals.debt_to_equity_percentile)).toFixed(0)}%
                            </span>
                          )}
                        </div>
                      </div>

                      <h4 className="subsection-title">
                        VALUE METRICS
                        <span className="composite-score">
                          Overall Value: {getQualitativeLabel(stockDetails.fundamentals.value_sector_percentile || selectedStock.value_percentile).text} {getQualitativeLabel(stockDetails.fundamentals.value_sector_percentile || selectedStock.value_percentile).emoji} ({(stockDetails.fundamentals.value_sector_percentile || selectedStock.value_percentile)?.toFixed(0)}th percentile in sector)
                        </span>
                      </h4>
                      <div className="raw-metrics-grid">
                        <div className="raw-metric">
                          <span className="raw-label">
                            PE Ratio
                            <span
                              className="tooltip-icon"
                              onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'pe_ratio' ? null : 'pe_ratio'); }}
                            >
                              ⓘ
                            </span>
                            {activeTooltip === 'pe_ratio' && (
                              <span className="tooltip-popup">{metricTooltips.pe_ratio}</span>
                            )}
                          </span>
                          <span className="raw-value">{formatNumber(stockDetails.fundamentals.pe_ratio, 1)}</span>
                          {stockDetails.fundamentals.pe_ratio_sector_percentile && (
                            <span className={`qual-label ${getQualitativeLabel(stockDetails.fundamentals.pe_ratio_sector_percentile).className}`}>
                              {getQualitativeLabel(stockDetails.fundamentals.pe_ratio_sector_percentile).text} {getQualitativeLabel(stockDetails.fundamentals.pe_ratio_sector_percentile).emoji} - {stockDetails.fundamentals.pe_ratio_sector_percentile < 50 ? `Bottom ${parseFloat(stockDetails.fundamentals.pe_ratio_sector_percentile).toFixed(0)}%` : `Top ${(100-parseFloat(stockDetails.fundamentals.pe_ratio_sector_percentile)).toFixed(0)}%`} in sector
                            </span>
                          )}
                        </div>
                        <div className="raw-metric">
                          <span className="raw-label">
                            PEG Ratio
                            <span
                              className="tooltip-icon"
                              onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'peg_ratio' ? null : 'peg_ratio'); }}
                            >
                              ⓘ
                            </span>
                            {activeTooltip === 'peg_ratio' && (
                              <span className="tooltip-popup">{metricTooltips.peg_ratio}</span>
                            )}
                          </span>
                          <span className="raw-value">{formatNumber(stockDetails.fundamentals.peg_ratio, 2)}</span>
                          {stockDetails.fundamentals.peg_ratio_sector_percentile && (
                            <span className={`qual-label ${getQualitativeLabel(stockDetails.fundamentals.peg_ratio_sector_percentile).className}`}>
                              {getQualitativeLabel(stockDetails.fundamentals.peg_ratio_sector_percentile).text} {getQualitativeLabel(stockDetails.fundamentals.peg_ratio_sector_percentile).emoji} - {stockDetails.fundamentals.peg_ratio_sector_percentile < 50 ? `Bottom ${parseFloat(stockDetails.fundamentals.peg_ratio_sector_percentile).toFixed(0)}%` : `Top ${(100-parseFloat(stockDetails.fundamentals.peg_ratio_sector_percentile)).toFixed(0)}%`} in sector
                            </span>
                          )}
                        </div>
                        <div className="raw-metric">
                          <span className="raw-label">
                            Price/Book
                            <span
                              className="tooltip-icon"
                              onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'price_to_book' ? null : 'price_to_book'); }}
                            >
                              ⓘ
                            </span>
                            {activeTooltip === 'price_to_book' && (
                              <span className="tooltip-popup">{metricTooltips.price_to_book}</span>
                            )}
                          </span>
                          <span className="raw-value">{formatNumber(stockDetails.fundamentals.price_to_book, 1)}</span>
                          {stockDetails.fundamentals.price_to_book_sector_percentile && (
                            <span className={`qual-label ${getQualitativeLabel(stockDetails.fundamentals.price_to_book_sector_percentile).className}`}>
                              {getQualitativeLabel(stockDetails.fundamentals.price_to_book_sector_percentile).text} {getQualitativeLabel(stockDetails.fundamentals.price_to_book_sector_percentile).emoji} - {stockDetails.fundamentals.price_to_book_sector_percentile < 50 ? `Bottom ${parseFloat(stockDetails.fundamentals.price_to_book_sector_percentile).toFixed(0)}%` : `Top ${(100-parseFloat(stockDetails.fundamentals.price_to_book_sector_percentile)).toFixed(0)}%`} in sector
                            </span>
                          )}
                        </div>
                        <div className="raw-metric">
                          <span className="raw-label">
                            Price/Sales
                            <span
                              className="tooltip-icon"
                              onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'price_to_sales' ? null : 'price_to_sales'); }}
                            >
                              ⓘ
                            </span>
                            {activeTooltip === 'price_to_sales' && (
                              <span className="tooltip-popup">{metricTooltips.price_to_sales}</span>
                            )}
                          </span>
                          <span className="raw-value">{formatNumber(stockDetails.fundamentals.price_to_sales, 1)}</span>
                          {stockDetails.fundamentals.price_to_sales_sector_percentile && (
                            <span className={`qual-label ${getQualitativeLabel(stockDetails.fundamentals.price_to_sales_sector_percentile).className}`}>
                              {getQualitativeLabel(stockDetails.fundamentals.price_to_sales_sector_percentile).text} {getQualitativeLabel(stockDetails.fundamentals.price_to_sales_sector_percentile).emoji} - {stockDetails.fundamentals.price_to_sales_sector_percentile < 50 ? `Bottom ${parseFloat(stockDetails.fundamentals.price_to_sales_sector_percentile).toFixed(0)}%` : `Top ${(100-parseFloat(stockDetails.fundamentals.price_to_sales_sector_percentile)).toFixed(0)}%`} in sector
                            </span>
                          )}
                        </div>
                      </div>

                      <h4 className="subsection-title">
                        GROWTH METRICS
                        <span className="composite-score">
                          Overall Growth: {getQualitativeLabel(selectedStock.growth_percentile).text} {getQualitativeLabel(selectedStock.growth_percentile).emoji} ({selectedStock.growth_percentile ? parseFloat(selectedStock.growth_percentile).toFixed(0) : null}th percentile)
                        </span>
                      </h4>
                        <div className="raw-metrics-grid">
                          <div className="raw-metric">
                            <span className="raw-label">
                              Revenue Growth
                              <span
                                className="tooltip-icon"
                                onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'revenue_growth' ? null : 'revenue_growth'); }}
                              >
                                ⓘ
                              </span>
                              {activeTooltip === 'revenue_growth' && (
                                <span className="tooltip-popup">{metricTooltips.revenue_growth}</span>
                              )}
                            </span>
                            <span className="raw-value">{formatPercent(stockDetails.fundamentals.revenue_growth)} per year</span>
                            {stockDetails.fundamentals.revenue_growth_percentile && (
                              <span className={`qual-label ${getQualitativeLabel(stockDetails.fundamentals.revenue_growth_percentile).className}`}>
                                {getQualitativeLabel(stockDetails.fundamentals.revenue_growth_percentile).text} {getQualitativeLabel(stockDetails.fundamentals.revenue_growth_percentile).emoji} - Top {(100-parseFloat(stockDetails.fundamentals.revenue_growth_percentile)).toFixed(0)}%
                              </span>
                            )}
                          </div>
                          <div className="raw-metric">
                            <span className="raw-label">
                              Earnings Growth
                              <span
                                className="tooltip-icon"
                                onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'earnings_growth' ? null : 'earnings_growth'); }}
                              >
                                ⓘ
                              </span>
                              {activeTooltip === 'earnings_growth' && (
                                <span className="tooltip-popup">{metricTooltips.earnings_growth}</span>
                              )}
                            </span>
                            <span className="raw-value">{formatPercent(stockDetails.fundamentals.earnings_growth)} per year</span>
                            {stockDetails.fundamentals.earnings_growth_percentile && (
                              <span className={`qual-label ${getQualitativeLabel(stockDetails.fundamentals.earnings_growth_percentile).className}`}>
                                {getQualitativeLabel(stockDetails.fundamentals.earnings_growth_percentile).text} {getQualitativeLabel(stockDetails.fundamentals.earnings_growth_percentile).emoji} - Top {(100-parseFloat(stockDetails.fundamentals.earnings_growth_percentile)).toFixed(0)}%
                              </span>
                            )}
                          </div>
                          <div className="raw-metric">
                            <span className="raw-label">Market Cap</span>
                            <span className="raw-value">{formatCurrency(stockDetails.fundamentals.market_cap)}</span>
                            <span className="qual-label qual-na">({stockDetails.fundamentals.market_cap_tier})</span>
                          </div>
                          <div className="raw-metric">
                            <span className="raw-label">Sector</span>
                            <span className="raw-value">{stockDetails.fundamentals.sector || '--'}</span>
                            <span className="qual-label qual-na">{stockDetails.fundamentals.industry || '--'}</span>
                          </div>
                        </div>

                        <div className="summary-text">{selectedStock.fundamental_summary}</div>
                    </div>
                  )}

                  {/* MOMENTUM TAB - WaveTrend Signals (same as wavetrend-signals page) */}
                  {activeTab === 'momentum' && (
                    <div className="modal-section">
                      <h3>📈 WaveTrend Momentum Signals</h3>

                    {stockDetails.wavetrendData?.strategies?.length > 0 ? (
                      <>
                        <div className="quality-score-badge">
                          Quality Score: <strong>{stockDetails.wavetrendData.ticker_quality_score || '--'}</strong>/100
                        </div>

                        <table className="signals-table wavetrend-table">
                          <thead>
                            <tr>
                              <th title="Strategy score combining quality and freshness">Score</th>
                              <th>Strategy</th>
                              <th title="Current wave: BUY (bullish) or SELL (bearish)">Zone</th>
                              <th title="Win rate for this ticker on this strategy">Win%</th>
                              <th title="Annualized return for this ticker">Ann.Ret</th>
                              <th title="Average return per trade">Avg/Trade</th>
                              <th title="Trades per year (15Y average)">Trades/Yr</th>
                              <th title="Most recent signal and days ago">Last Signal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stockDetails.wavetrendData.strategies.map((strategy, idx) => {
                              const score = strategy.strategy_score || 0;
                              const scoreColor = score >= 60 ? 'score-high' : score >= 40 ? 'score-medium' : 'score-low';
                              const winRate = strategy.ticker_success_rate != null ? `${parseFloat(strategy.ticker_success_rate).toFixed(1)}%` : '--';
                              const annReturn = strategy.ticker_annualized_return != null ? `${parseFloat(strategy.ticker_annualized_return).toFixed(1)}%` : '--';
                              const avgPerTrade = strategy.ticker_avg_return_per_trade != null ? `${parseFloat(strategy.ticker_avg_return_per_trade).toFixed(2)}%` : '--';
                              const tradesPerYear = strategy.ticker_num_trades != null ? (strategy.ticker_num_trades / 15).toFixed(1) : '--';
                              const lastSignalDays = strategy.last_signal_days_ago;
                              const lastSignalType = strategy.last_signal_type;
                              const zone = strategy.zone;
                              // Signal is stale if older than 3 days (from API) or calculated here as fallback
                              const isStale = strategy.is_stale ?? (lastSignalDays != null && lastSignalDays > 3);

                              return (
                                <tr key={idx} className={score >= 60 ? 'high-score-row' : ''}>
                                  <td className={`score-cell ${scoreColor}`}>{score}</td>
                                  <td className="strategy-name">
                                    {strategy.strategy_name || strategy.strategy_type?.replace('wavetrend_', '').replace(/_/g, ' → ')}
                                    {strategy.signal_strength === 'moderate' && <span className="strength-badge moderate">(M)</span>}
                                  </td>
                                  <td className="zone-cell">
                                    {isStale ? <span className="zone-badge zone-none">--</span> :
                                     zone === 'BUY' ? <span className="zone-badge zone-buy">BUY</span> :
                                     zone === 'SELL' ? <span className="zone-badge zone-sell">SELL</span> :
                                     <span className="zone-badge zone-none">--</span>}
                                  </td>
                                  <td className={`win-rate ${(strategy.ticker_success_rate || 0) >= 60 ? 'rate-high' : (strategy.ticker_success_rate || 0) >= 50 ? 'rate-medium' : 'rate-low'}`}>
                                    {winRate}
                                  </td>
                                  <td className={`ann-return ${(strategy.ticker_annualized_return || 0) >= 0 ? 'return-positive' : 'return-negative'}`}>
                                    {annReturn}
                                  </td>
                                  <td className={`avg-trade ${(strategy.ticker_avg_return_per_trade || 0) >= 0 ? 'return-positive' : 'return-negative'}`}>
                                    {avgPerTrade}
                                  </td>
                                  <td className="trades-year">{tradesPerYear}</td>
                                  <td className="last-signal">
                                    {lastSignalType && lastSignalDays != null ? (
                                      <>
                                        <span className={`signal-type ${lastSignalType.toLowerCase()}`}>{lastSignalType}</span>
                                        <span className={lastSignalDays <= 7 ? 'age-fresh' : 'age-old'}> {lastSignalDays}d ago</span>
                                      </>
                                    ) : '--'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        <div className="summary-text">{selectedStock.momentum_summary}</div>
                      </>
                    ) : (
                      <div className="summary-text">No WaveTrend signals available for this stock.</div>
                    )}
                    </div>
                  )}

                  {/* Recommendation - shown on all tabs */}
                  <div className="modal-section">
                    <h3>💡 AI Recommendation</h3>
                    <div className="summary-text">{selectedStock.recommendation_reason}</div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FundamentalSignals;
